import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { getOcrHealth } from '@/services/pos-api';
const COLORS = {
    healthy: 'var(--mc-green)',
    loading: 'var(--mc-amber)',
    offline: 'var(--mc-magenta)',
    unknown: 'var(--mc-text-meta-dim)',
};
function statusFromHealth(health) {
    if (!health)
        return 'unknown';
    if (!health.reachable)
        return 'offline';
    if (health.modelLoaded === false)
        return 'loading';
    return 'healthy';
}
/**
 * Tiny pulsing dot that surfaces Pi5 OCR service health.
 * Rendered in the command bar for ADMINs. Polls /api/receipt-analysis/health.
 */
export function MCHealthDot({ intervalMs = 30000, enabled = true, health, lastProbe, }) {
    const usesExternalHealth = health !== undefined;
    const [internalHealth, setInternalHealth] = useState(null);
    const [internalLastProbe, setInternalLastProbe] = useState(null);
    const [pendingRetries, setPendingRetries] = useState(0);
    const activeHealth = usesExternalHealth ? health ?? null : internalHealth;
    const activeLastProbe = usesExternalHealth ? lastProbe ?? null : internalLastProbe;
    const status = statusFromHealth(activeHealth);
    useEffect(() => {
        if (usesExternalHealth)
            return;
        if (!enabled)
            return;
        let cancelled = false;
        const probe = async () => {
            try {
                const data = await getOcrHealth();
                if (cancelled)
                    return;
                setInternalHealth(data);
                setInternalLastProbe(new Date());
                setPendingRetries(typeof data.pendingRetries === 'number' ? data.pendingRetries : 0);
            }
            catch {
                if (!cancelled) {
                    setInternalHealth({ reachable: false, error: 'Health check failed' });
                    setInternalLastProbe(new Date());
                }
            }
        };
        probe();
        const handle = window.setInterval(probe, intervalMs);
        return () => {
            cancelled = true;
            window.clearInterval(handle);
        };
    }, [intervalMs, enabled, usesExternalHealth]);
    useEffect(() => {
        if (!usesExternalHealth)
            return;
        setPendingRetries(typeof activeHealth?.pendingRetries === 'number' ? activeHealth.pendingRetries : 0);
    }, [activeHealth, usesExternalHealth]);
    if (!enabled)
        return null;
    const tooltip = [
        `OCR: ${status}`,
        activeHealth?.modelLoaded === false ? 'Model loading' : null,
        activeHealth?.responseTimeMs != null ? `${activeHealth.responseTimeMs}ms` : null,
        activeHealth?.error ? activeHealth.error : null,
        activeLastProbe ? `Last probe: ${activeLastProbe.toLocaleTimeString('en-US', { timeZone: 'America/Halifax' })}` : null,
        pendingRetries > 0 ? `${pendingRetries} pending retries` : null,
    ]
        .filter(Boolean)
        .join(' · ');
    return (_jsxs("div", { className: "flex items-center gap-2 px-3 py-1 rounded-sm", style: {
            border: '1px solid var(--mc-divider)',
            background: 'transparent',
        }, title: tooltip, "aria-label": tooltip, children: [_jsx("span", { className: `inline-block w-1.5 h-1.5 rounded-full ${status === 'healthy' ? 'mc-pulse' : ''}`, style: { background: COLORS[status] }, "aria-hidden": true }), _jsx("span", { className: "mc-mono text-[11px] text-[color:var(--mc-text-meta)] uppercase tracking-wider", children: "OCR" })] }));
}
